import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";

import CustomModal from "../customModal";
import CountryDialSelect from "../countryDialCodes";
import { CircleSpinner } from "../Loaders";
import { sendAxiosRequestWithRetry } from "../../services/sendAxiosRequestWithRetry";
import { normalizeDialCodeSymbol, normalizePhoneLocalNumber, normalizePhoneStateOnBlur } from "../../helpers/phone";
import { useDispatch, useSelector } from "react-redux";
import { setShowSendMenuLinkModal } from "../../store/slices/appGeneralSlice";
import { getVirtualKeyboardTargetProps } from "../../helpers/virtualKeyboard";
import { getApiErrorText } from "../../shared/api/errors";

interface SendMenuLinkModalProps {
    onHide?: () => void;
}

const SendMenuLinkModal = ({
    onHide,
}: SendMenuLinkModalProps) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const show = useSelector((state: RootState) => state.appGeneral.showSendMenuLinModal);
    const showKeyboard = useSelector((state: RootState) => state.appGeneral.showKeyboard);
    const telKeyboardProps = getVirtualKeyboardTargetProps("tel", showKeyboard);

    const [countrySymbol, setCountrySymbol] = useState("at");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [submitError, setSubmitError] = useState<string>();
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        if (!show) return;
        setCountrySymbol("at");
        setPhoneNumber("");
        setSubmitError("");
    }, [show]);

    const handleClose = () => {
        if (showLoader) return;
        setSubmitError("");
        dispatch(setShowSendMenuLinkModal(false));
        onHide?.();
    };

    const handlePhoneBlur = () => {
        const normalizedPhoneState = normalizePhoneStateOnBlur({
            phoneNumber,
            countrySymbol,
            fallbackCountrySymbol: "at",
        });
        const nextCountrySymbol = normalizedPhoneState.countrySymbol || "at";
        const nextPhoneNumber = normalizedPhoneState.phoneNumber || "";

        if (nextCountrySymbol !== countrySymbol) {
            setCountrySymbol(nextCountrySymbol);
        }
        if (nextPhoneNumber !== `${phoneNumber || ""}`.trim()) {
            setPhoneNumber(nextPhoneNumber);
        }
    };

    const handleSubmit = async () => {
        if (showLoader) return;

        const normalizedCountrySymbol = normalizeDialCodeSymbol(countrySymbol, "at");
        const normalizedPhoneNumber = normalizePhoneLocalNumber(
            phoneNumber,
            normalizedCountrySymbol
        ).trim();

        if (!normalizedPhoneNumber) {
            setSubmitError(String(t("phoneNumberIsRequired")));
            return;
        }

        setSubmitError("");
        setShowLoader(true);

        const {
            data: respData,
            error: respError
        } = await sendAxiosRequestWithRetry({
            method: "post",
            url: "/order/send_menu_link",
            data: {
                phone_number: {
                    number: normalizedPhoneNumber,
                    country_symbol: normalizedCountrySymbol,
                }
            }
        });

        setShowLoader(false);

        if (respData) {
            toast.success(t("menuLinkSentSuccessfully"));
            setSubmitError("");
            dispatch(setShowSendMenuLinkModal(false));
            onHide?.();
            return;
        }
        else if (respError) {
            const errMessage = getApiErrorText(respError);

            setSubmitError(errMessage);
            toast.error(errMessage);
        }
    };

    return (
        <CustomModal
            // size="md"
            show={show}
            dialogClassName="send-menu-link-modal-dialog"
            onHide={handleClose}
            className="menu-link-sms-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title className="fw-normal text-capitalize">
                    {t("sendMenuLinkBySms")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body
                className="py-4 my-1 px-3"
            >
                <div className="text-start">
                    <label className="form-label fw-bold mb-1 required">
                        {t("receiverPhone")}
                    </label>

                    <div className="d-flex phone-inputs-row">
                        <div className="country-code">
                            <CountryDialSelect
                                defaultCountry={countrySymbol || "at"}
                                className="country-select"
                                classNamePrefix="country-select"
                                menuPlacement="auto"
                                onChange={(option) => {
                                    setCountrySymbol(option?.value ?? "at");
                                    setSubmitError("");
                                }}
                            />
                        </div>

                        <input
                            {...telKeyboardProps}
                            type="text"
                            className="form-control phone-input"
                            required={true}
                            value={phoneNumber}
                            onChange={(e) => {
                                setPhoneNumber(e.target.value);
                                setSubmitError("");
                            }}
                            onBlur={handlePhoneBlur}
                        />
                    </div>
                </div>

                {submitError && (
                    <p className="mb-0 mt-2">
                        <small className="text-red">
                            <em>{submitError}</em>
                        </small>
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer className="text-end">
                <button
                    type="button"
                    className="btn btn-outline-secondary fs-6 fw-bold"
                    onClick={handleClose}
                    disabled={showLoader}
                >
                    {t("cancel")}
                </button>
                <button
                    type="button"
                    className="btn btn-dark fs-6 fw-bold d-inline-flex align-items-center"
                    onClick={handleSubmit}
                    disabled={showLoader}
                >
                    {showLoader && (
                        <span className="menu-link-sms-loader me-2">
                            <CircleSpinner
                                circleOpacity={0}
                                spinnerColor="#fff"
                                thickness={6}
                            />
                        </span>
                    )}
                    {t("send")}
                </button>
            </Modal.Footer>
        </CustomModal>
    );
};

export default SendMenuLinkModal;
